import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { FontWeight, Heading, Text } from '@lili/lili-frontend-common';
import { AppCenterNoResults } from '../../../assets/svg';
import { ASSETS_BASE_URL } from '../../../constants/endpoints';
import Icon from '../../../generic-components/Icon';
import MainContent from '../../../generic-components/Layout/MainContent';
import SubContent from '../../../generic-components/Layout/SubContent';
import { useAppCenter } from '../../../hooks/query/useAppCenterQuery';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { IAppCenterCategory, IVendor } from '../../../types';
import SearchBar from '../components/searchBar/SearchBar';
import VendorTile from '../components/vendorTile/VendorTile';
import { APP_CENTER_TEXTS } from '../constants';
import PageLayout from '../../../generic-components/PageLayout/PageLayout';

let lastPage = 0;

const AppCenter = () => {
  const { isLoading, refetch } = useAppCenter();
  const { t } = useTranslation();

  const getVendorImage = (logoUrl: string) => `${ASSETS_BASE_URL}/unified/appcenter/${logoUrl}@2x.png`;
  const [categoriesList, setCategoriesList] = useState<IAppCenterCategory[]>([]);
  const [originalCategoriesList, setOriginalCategoriesList] = useState<IAppCenterCategory[]>([]);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState<number>(0);
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchResults, setSearchResults] = useState<IVendor[]>([]);

  useEffect(() => {
    // check after navigate back from instruction page, in which page we are had been before
    if (lastPage !== 0) {
      setSelectedCategoryIndex(lastPage);
    }
    refetch().then((res) => {
      setCategoriesList(res.data.categories);
      setOriginalCategoriesList(res.data.categories);
    });
    trackMixPanel('View AppCenter Hub Screen', {
      Origin: 'Menu Screen',
    });
  }, []);

  const renderNoResults = () => (
    <div className='w-full h-full flex flex-col justify-center items-center gap-6'>
      <Icon src={AppCenterNoResults} />
      <p className='text-[14px] text-bw06'>{t(APP_CENTER_TEXTS.NO_RESULTS)}</p>
    </div>
  );

  const renderVendorList = (vendors: IVendor[]) =>
    (vendors.length > 0
      ? vendors.map((vendor, index: number) => (
        <div key={vendor.id} id={`vendorTile-${index}`}>
          <VendorTile vendorDetails={vendor} vendorImage={getVendorImage(vendor.logoUrl)} />
        </div>
      ))
      : renderNoResults());

  return (
    <PageLayout>
      <div className='flex items-baseline mb-[10px]'>
        <Heading.H5 fontWeight={FontWeight.MEDIUM}>{t('APP_CENTER_TITLE')}</Heading.H5>
        <SearchBar
          setCategoriesList={setCategoriesList}
          originalList={originalCategoriesList || []}
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          setSearchResults={setSearchResults}
          selectedCategoryIndex={selectedCategoryIndex}
        />
      </div>
      <div className='w-full h-full flex gap-[24px]'>
        <SubContent className='flex flex-col items-center bg-bw00 rounded-xl min-w-[271px]  py-[1.3rem] h-[35.125rem] pc-lg:h-[90%] pc-lg:min-h-[750px] shadow-widgetCard border-[1px] border-outline-primary-bw-03'>
          {isLoading ? (
            <div className='w-full h-full'>
              <Skeleton className='my-4' count={9} />
            </div>
          ) : (
            <ul className='text-left w-full relative'>
              {categoriesList &&
                categoriesList.map((category, index: number) => (
                  <li
                    role='presentation'
                    id={`appCenterCategory${index}`}
                    key={category.categoryId}
                    className='min-h-[60px] px-[16px] py-2 cursor-pointer flex flex-col justify-center border-b border-outline-primary-bw-03 hover:bg-surface-primary-bw-01'
                    onClick={() => {
                      setSelectedCategoryIndex(index);
                      lastPage = index;
                    }}
                  >
                    {index === selectedCategoryIndex && (
                      <div className='absolute left-[0px] w-[4px] h-[60px] bg-surface-brand-action-03' />
                    )}
                    <Heading.H9 fontWeight={index === selectedCategoryIndex ? FontWeight.SEMIBOLD : FontWeight.REGULAR}>

                      {category.vendorCategoryName}
                    </Heading.H9>
                  </li>
                ))}
            </ul>
          )}
        </SubContent>
        <MainContent className='w-full'>
          <div className='flex flex-col bg-bw00 rounded-xl flex-grow px-[31px] pt-[48px] h-[35.125rem] pc-lg:h-[90%] pc-lg:min-h-[750px] shadow-widgetCard border-[1px] border-outline-primary-bw-03'>
            {isLoading ? (
              <div className='w-full h-full'>
                <Skeleton className='my-4' count={9} />
              </div>
            ) : (
              <div className='h-full'>
                {categoriesList[selectedCategoryIndex] && searchInput === '' && (
                  <>
                    <Text.H9 testId='appCenterHeader' className='w-[447px]'>
                      {categoriesList[selectedCategoryIndex].vendorCategoryDescription}
                    </Text.H9>
                    {categoriesList[selectedCategoryIndex].vendors.length === 0 ? (
                      renderNoResults()
                    ) : (
                      <div className='flex gap-6 mt-6 w-full flex-wrap h-[120px]'>
                        {renderVendorList(categoriesList[selectedCategoryIndex].vendors)}
                      </div>
                    )}
                  </>
                )}
                {searchInput !== '' && (
                  <div className='flex gap-6 w-full flex-wrap h-[120px]'>{renderVendorList(searchResults)}</div>
                )}
              </div>
            )}
          </div>
        </MainContent>
      </div>
    </PageLayout>
  );
};

export default AppCenter;
